import { logger } from "@amzn/afxtaco-utilities";
import { AppDispatch } from "../../../redux/store";
import {
  setAcknowledged,
  addProgress,
  appendStreamingReasoning,
  appendStreamingContent,
  addAssetUpdate,
  addSystemEvent,
  setError,
  AcknowledgementStatus,
  CompletionStatus,
} from "../../../redux/reducers/Execution";
import { handleExecutionCompleted } from "../../../redux/thunks/executionThunks";

// ---------------------------------------------------------------------------
// Message types
// ---------------------------------------------------------------------------

/**
 * All recognized WebSocket message type values.
 *
 * Sources:
 * - AFXTACOWebsocketLambda  → acknowledgement, error
 * - AFXTACOOrchestratorLambda → progress, error, complete
 * - AFXTACOAgent WebSocketStreamer → text_delta, reasoning_delta, complete, error
 */
enum WebSocketMessageType {
  // Orchestrator / WebSocket Lambda types
  ACKNOWLEDGEMENT = "acknowledgement",
  PROGRESS = "progress",
  AGENT_RESPONSE = "agent_response",
  ASSET_UPDATE = "asset_update",
  COMPLETE = "complete",
  ERROR = "error",
  // Agent streaming types (sent directly by AFXTACOAgent via WebSocketStreamer)
  TEXT_DELTA = "text_delta",
  REASONING_DELTA = "reasoning_delta",
  SYSTEM_EVENT = "system_event",
}

// ---------------------------------------------------------------------------
// Base message interface
// ---------------------------------------------------------------------------

/**
 * Base interface for all server-to-client WebSocket messages.
 *
 * All fields except `type` are optional because different backend senders
 * include different fields:
 * - `clientRequestId` is present on Orchestrator/Lambda messages but absent on Agent messages.
 * - `data` is present on Agent messages (WebSocketStreamer) but absent on Orchestrator/Lambda messages.
 */
interface WebSocketServerMessage {
  type: string;
  clientRequestId?: string;
  projectId?: string;
  userAlias?: string;
  sessionId?: string;
  data?: unknown;
}

// ---------------------------------------------------------------------------
// Message interfaces — Orchestrator / WebSocket Lambda
// ---------------------------------------------------------------------------

interface AcknowledgementMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.ACKNOWLEDGEMENT;
  clientRequestId: string;
  status: "QUEUED" | "DUPLICATE";
}

interface ProgressMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.PROGRESS;
  clientRequestId: string;
  message: string;
  step?: number;
  totalSteps?: number;
}

interface AgentResponseMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.AGENT_RESPONSE;
  clientRequestId: string;
  content: string;
  isPartial: boolean;
}

interface AssetUpdateMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.ASSET_UPDATE;
  /** Project-relative path of the changed asset, e.g. `summary/summary.md`. */
  path: string;
  assetType?: string;
  tabLabel?: string;
  s3Key?: string;
}

/** Orchestrator complete — flat shape with `status` field. */
interface OrchestratorCompleteMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.COMPLETE;
  clientRequestId: string;
  status: "COMPLETED" | "FAILED";
  version?: number;
}

/** Orchestrator/Lambda error — flat shape with `errorCode` and `errorMessage`. */
interface OrchestratorErrorMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.ERROR;
  clientRequestId: string;
  errorCode: string;
  errorMessage: string;
}

// ---------------------------------------------------------------------------
// Message interfaces — Agent WebSocketStreamer
// ---------------------------------------------------------------------------

/** Agent text_delta — streaming chat content chunk. */
interface AgentTextDeltaMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.TEXT_DELTA;
  data: { text: string };
}

/** Agent reasoning_delta — extended thinking/reasoning chunk. */
interface AgentReasoningDeltaMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.REASONING_DELTA;
  data: { text: string };
}

/** Agent system_event — tool call lifecycle event for the Thinking tab. */
interface AgentSystemEventMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.SYSTEM_EVENT;
  data: { eventType: string; toolName?: string; details?: string };
}

/** Agent complete — wrapped shape with `data.text` (final full response). */
interface AgentCompleteMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.COMPLETE;
  data: { text: string };
}

/** Agent error — wrapped shape with `data.error`. */
interface AgentErrorMessage extends WebSocketServerMessage {
  type: WebSocketMessageType.ERROR;
  data: { error: string };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if the message has a `data` object — indicating it originated
 * from the Agent's WebSocketStreamer rather than the Orchestrator or WebSocket Lambda.
 */
function isAgentWrappedMessage(payload: WebSocketServerMessage): boolean {
  return typeof payload.data === "object" && payload.data !== null;
}

// ---------------------------------------------------------------------------
// Handler
// ---------------------------------------------------------------------------

/**
 * Handles incoming WebSocket messages and dispatches appropriate Redux actions
 * based on the server message type.
 *
 * Messages arrive from three backend sources, each with its own shapes:
 *
 * **AFXTACOWebsocketLambda:**
 * - `acknowledgement` — request queued or duplicate
 * - `error` — validation/processing error (flat shape)
 *
 * **AFXTACOOrchestratorLambda:**
 * - `progress` — retry or processing status
 * - `complete` — execution finished (flat shape with `status`)
 * - `error` — orchestration failed (flat shape with `errorCode`)
 *
 * **AFXTACOAgent WebSocketStreamer:**
 * - `text_delta` — streaming chat content chunk (in `data.text`)
 * - `reasoning_delta` — extended thinking chunk (in `data.text`)
 * - `complete` — agent finished (wrapped in `data.text`)
 * - `error` — agent error (wrapped in `data.error`)
 *
 * For `complete` and `error`, Agent messages are distinguished from
 * Orchestrator messages by the presence of a `data` object wrapper.
 *
 * Heartbeat "pong" responses ({@code {"type":"pong"}}) are silently ignored.
 */
export const webSocketOnMessageEventHandler = (
  dispatch: AppDispatch,
  event: MessageEvent,
): void => {
  if (!event.data) {
    return;
  }

  let payload: WebSocketServerMessage;
  try {
    payload = JSON.parse(event.data) as WebSocketServerMessage;
  } catch {
    logger.warn({
      loggerMsg: "WebSocket: Failed to parse message as JSON",
      rawData: event.data,
    });
    //TODO: add metric here
    return;
  }

  // Heartbeat pong responses (JSON: {"type":"pong"}) — log and ignore.
  if (payload?.type === "pong") {
    logger.info({
      loggerMsg: "WebSocket: Heartbeat pong received",
    });
    return;
  }

  if (!payload || !payload.type) {
    logger.warn({
      loggerMsg: "WebSocket: Message missing 'type' field",
      payload,
    });
    return;
  }

  switch (payload.type) {
    case WebSocketMessageType.ACKNOWLEDGEMENT: {
      const msg = payload as AcknowledgementMessage;
      logger.log({
        loggerMsg: "WebSocket: Acknowledgement received",
        status: msg.status,
        clientRequestId: msg.clientRequestId,
      });
      dispatch(
        setAcknowledged({ status: msg.status as AcknowledgementStatus }),
      );
      break;
    }

    case WebSocketMessageType.PROGRESS: {
      const msg = payload as ProgressMessage;
      logger.log({
        loggerMsg: "WebSocket: Progress update",
        message: msg.message,
        step: msg.step,
        totalSteps: msg.totalSteps,
        clientRequestId: msg.clientRequestId,
      });
      dispatch(
        addProgress({
          message: msg.message,
          step: msg.step,
          totalSteps: msg.totalSteps,
          timestamp: new Date().toISOString(),
        }),
      );
      break;
    }

    case WebSocketMessageType.AGENT_RESPONSE: {
      const msg = payload as AgentResponseMessage;
      logger.log({
        loggerMsg: "WebSocket: Agent response",
        isPartial: msg.isPartial,
        contentLength: msg.content?.length,
        clientRequestId: msg.clientRequestId,
      });
      dispatch(
        appendStreamingContent({
          content: msg.content,
          isPartial: msg.isPartial,
        }),
      );
      break;
    }

    case WebSocketMessageType.TEXT_DELTA: {
      const msg = payload as AgentTextDeltaMessage;
      logger.log({
        loggerMsg: "WebSocket: Agent text_delta",
        contentLength: msg.data?.text?.length,
      });
      dispatch(
        appendStreamingContent({
          content: msg.data.text,
          isPartial: true,
        }),
      );
      break;
    }

    case WebSocketMessageType.REASONING_DELTA: {
      const msg = payload as AgentReasoningDeltaMessage;
      logger.log({
        loggerMsg: "WebSocket: Agent reasoning_delta",
        contentLength: msg.data?.text?.length,
      });
      dispatch(
        appendStreamingReasoning({
          text: msg.data.text,
        }),
      );
      break;
    }

    case WebSocketMessageType.SYSTEM_EVENT: {
      const msg = payload as AgentSystemEventMessage;
      logger.log({
        loggerMsg: "WebSocket: Agent system_event",
        eventType: msg.data.eventType,
        toolName: msg.data.toolName,
      });
      dispatch(
        addSystemEvent({
          eventType: msg.data.eventType,
          toolName: msg.data.toolName,
          details: msg.data.details,
          timestamp: new Date().toISOString(),
        }),
      );
      break;
    }

    case WebSocketMessageType.ASSET_UPDATE: {
      const msg = payload as AssetUpdateMessage;
      logger.log({
        loggerMsg: "WebSocket: Asset update",
        path: msg.path,
        assetType: msg.assetType,
        tabLabel: msg.tabLabel,
        clientRequestId: msg.clientRequestId,
      });
      dispatch(
        addAssetUpdate({
          path: msg.path,
          assetType: msg.assetType,
          tabLabel: msg.tabLabel ?? msg.assetType ?? msg.path,
          timestamp: new Date().toISOString(),
        }),
      );
      break;
    }

    case WebSocketMessageType.COMPLETE: {
      if (isAgentWrappedMessage(payload)) {
        // Agent: { type: "complete", data: { text: "..." } }.
        // No-op by design: `data.text` is the full turn transcript and would
        // overwrite the per-bubble segmentation built up from `text_delta` +
        // `system_event` boundaries. The COMPLETED state transition is driven
        // by the orchestrator's `complete` message; we only log here.
        const msg = payload as AgentCompleteMessage;
        logger.log({
          loggerMsg: "WebSocket: Agent complete (stream done, payload ignored)",
          contentLength: msg.data.text?.length,
        });
      } else {
        // Orchestrator: { type: "complete", status: "COMPLETED", version: N }
        const msg = payload as OrchestratorCompleteMessage;
        logger.log({
          loggerMsg: "WebSocket: Execution complete",
          status: msg.status,
          version: msg.version,
          clientRequestId: msg.clientRequestId,
        });
        // Dispatch the centralized thunk that handles the full completion
        // lifecycle: set COMPLETED → fetch project metadata → invalidate
        // asset caches → reset to IDLE.
        dispatch(
          handleExecutionCompleted({
            status: msg.status as CompletionStatus,
            version: msg.version,
          }),
        );
      }
      break;
    }

    case WebSocketMessageType.ERROR: {
      if (isAgentWrappedMessage(payload)) {
        // Agent: { type: "error", data: { error: "..." } }
        const msg = payload as AgentErrorMessage;
        logger.error({
          loggerMsg: "WebSocket: Agent error received",
          errorMessage: msg.data.error,
        });
        dispatch(
          setError({
            errorCode: "AGENT_ERROR",
            errorMessage: msg.data.error,
          }),
        );
      } else {
        // Orchestrator/Lambda: { type: "error", errorCode: "...", errorMessage: "..." }
        const msg = payload as OrchestratorErrorMessage;
        logger.error({
          loggerMsg: "WebSocket: Error received",
          errorCode: msg.errorCode,
          errorMessage: msg.errorMessage,
          clientRequestId: msg.clientRequestId,
        });
        dispatch(
          setError({
            errorCode: msg.errorCode,
            errorMessage: msg.errorMessage,
          }),
        );
      }
      break;
    }

    default: {
      logger.warn({
        loggerMsg: "WebSocket: Unrecognized message type",
        type: payload.type,
        payload,
      });
    }
  }
};
